// filepath: src/sentiment.ts
import Sentiment from "sentiment"; // Import the Sentiment class using ES modules
import emojiRegex from "emoji-regex";

const sentiment = new Sentiment(); // Create an instance of Sentiment

/**
 * Emotion types that can be detected in text
 */
export type EmotionType =
  | "joy"
  | "sadness"
  | "anger"
  | "fear"
  | "surprise"
  | "disgust"
  | "neutral";

/**
 * Intensity levels for emotions
 */
export type IntensityLevel = "low" | "medium" | "high";

/**
 * Individual emotion with intensity and confidence
 */
export interface Emotion {
  type: EmotionType;
  intensity: IntensityLevel;
  score: number; // Raw score for this emotion
}

/**
 * Enhanced sentiment analysis result
 */
export interface SentimentResult {
  score: number; // Overall sentiment score (-5 to +5)
  comparative: number; // Comparative sentiment score (normalized by word count)
  emotions: Emotion[]; // Detected emotions sorted by score
  dominantEmotion: EmotionType; // The strongest detected emotion
  confidence: number; // Confidence in the analysis (0-1)
}

/**
 * Named constants for emoji sentiment scores
 * Extracted for consistency and maintainability
 */
const EMOJI_SCORES = {
  // Positive sentiment scores
  HIGH_POSITIVE: 4, // Very happy, loving, excited (😍, ❤️, 😂)
  MEDIUM_POSITIVE: 3, // Happy, cheerful (😊, 😀, 🎉)
  LOW_POSITIVE: 2, // Mildly positive (👍, ✨, 🤯)
  SLIGHT_POSITIVE: 1, // Slightly positive (😮, 😯)

  // Neutral scores
  NEUTRAL: 0, // Neutral, thinking (🤔, 😐, 😳)

  // Negative sentiment scores
  SLIGHT_NEGATIVE: -1, // Slightly negative (🙄)
  LOW_NEGATIVE: -2, // Mildly negative (😕, 😧, 😷)
  MEDIUM_NEGATIVE: -3, // Moderately negative (😢, 😠, 🤢)
  HIGH_NEGATIVE: -4, // Very negative (😭, 😡, 😱, 🤮, 💔)
  VERY_HIGH_NEGATIVE: -5, // Extremely negative (🤬)
} as const;

/**
 * Emoji sentiment mappings
 * Maps emoji to their sentiment values and associated emotions
 */
const EMOJI_SENTIMENT: Record<
  string,
  { score: number; emotions: EmotionType[] }
> = {
  // Joy/Happiness
  "😊": { score: EMOJI_SCORES.MEDIUM_POSITIVE, emotions: ["joy"] },
  "😀": { score: EMOJI_SCORES.MEDIUM_POSITIVE, emotions: ["joy"] },
  "😃": { score: EMOJI_SCORES.MEDIUM_POSITIVE, emotions: ["joy"] },
  "😄": { score: EMOJI_SCORES.MEDIUM_POSITIVE, emotions: ["joy"] },
  "😁": { score: EMOJI_SCORES.MEDIUM_POSITIVE, emotions: ["joy"] },
  "😆": { score: EMOJI_SCORES.HIGH_POSITIVE, emotions: ["joy"] },
  "😂": { score: EMOJI_SCORES.HIGH_POSITIVE, emotions: ["joy"] },
  "🤣": { score: EMOJI_SCORES.HIGH_POSITIVE, emotions: ["joy"] },
  "😍": { score: EMOJI_SCORES.HIGH_POSITIVE, emotions: ["joy"] },
  "🥰": { score: EMOJI_SCORES.HIGH_POSITIVE, emotions: ["joy"] },
  "😘": { score: EMOJI_SCORES.MEDIUM_POSITIVE, emotions: ["joy"] },
  "❤️": { score: EMOJI_SCORES.HIGH_POSITIVE, emotions: ["joy"] },
  "💕": { score: EMOJI_SCORES.HIGH_POSITIVE, emotions: ["joy"] },
  "💖": { score: EMOJI_SCORES.HIGH_POSITIVE, emotions: ["joy"] },
  "🎉": { score: EMOJI_SCORES.MEDIUM_POSITIVE, emotions: ["joy"] },
  "🎊": { score: EMOJI_SCORES.MEDIUM_POSITIVE, emotions: ["joy"] },
  "👍": { score: EMOJI_SCORES.LOW_POSITIVE, emotions: ["joy"] },
  "👏": { score: EMOJI_SCORES.LOW_POSITIVE, emotions: ["joy"] },
  "🙌": { score: EMOJI_SCORES.MEDIUM_POSITIVE, emotions: ["joy"] },
  "✨": { score: EMOJI_SCORES.LOW_POSITIVE, emotions: ["joy"] },
  "⭐": { score: EMOJI_SCORES.LOW_POSITIVE, emotions: ["joy"] },
  "🌟": { score: EMOJI_SCORES.LOW_POSITIVE, emotions: ["joy"] },

  // Sadness
  "😢": { score: EMOJI_SCORES.MEDIUM_NEGATIVE, emotions: ["sadness"] },
  "😭": { score: EMOJI_SCORES.HIGH_NEGATIVE, emotions: ["sadness"] },
  "😞": { score: EMOJI_SCORES.MEDIUM_NEGATIVE, emotions: ["sadness"] },
  "😔": { score: EMOJI_SCORES.MEDIUM_NEGATIVE, emotions: ["sadness"] },
  "😟": { score: EMOJI_SCORES.LOW_NEGATIVE, emotions: ["sadness", "fear"] },
  "😕": { score: EMOJI_SCORES.LOW_NEGATIVE, emotions: ["sadness"] },
  "☹️": { score: EMOJI_SCORES.LOW_NEGATIVE, emotions: ["sadness"] },
  "🙁": { score: EMOJI_SCORES.LOW_NEGATIVE, emotions: ["sadness"] },
  "😣": { score: EMOJI_SCORES.MEDIUM_NEGATIVE, emotions: ["sadness"] },
  "😖": { score: EMOJI_SCORES.MEDIUM_NEGATIVE, emotions: ["sadness"] },
  "😫": { score: EMOJI_SCORES.MEDIUM_NEGATIVE, emotions: ["sadness"] },
  "😩": { score: EMOJI_SCORES.MEDIUM_NEGATIVE, emotions: ["sadness"] },
  "🥺": { score: EMOJI_SCORES.LOW_NEGATIVE, emotions: ["sadness"] },
  "😿": { score: EMOJI_SCORES.MEDIUM_NEGATIVE, emotions: ["sadness"] },
  "💔": { score: EMOJI_SCORES.HIGH_NEGATIVE, emotions: ["sadness"] },

  // Anger
  "😠": { score: EMOJI_SCORES.MEDIUM_NEGATIVE, emotions: ["anger"] },
  "😡": { score: EMOJI_SCORES.HIGH_NEGATIVE, emotions: ["anger"] },
  "🤬": { score: EMOJI_SCORES.VERY_HIGH_NEGATIVE, emotions: ["anger"] },
  "😤": { score: EMOJI_SCORES.MEDIUM_NEGATIVE, emotions: ["anger"] },
  "💢": { score: EMOJI_SCORES.MEDIUM_NEGATIVE, emotions: ["anger"] },
  "👿": { score: EMOJI_SCORES.HIGH_NEGATIVE, emotions: ["anger"] },

  // Fear/Anxiety
  "😨": { score: EMOJI_SCORES.MEDIUM_NEGATIVE, emotions: ["fear"] },
  "😰": { score: EMOJI_SCORES.MEDIUM_NEGATIVE, emotions: ["fear"] },
  "😱": { score: EMOJI_SCORES.HIGH_NEGATIVE, emotions: ["fear"] },
  "😧": { score: EMOJI_SCORES.LOW_NEGATIVE, emotions: ["fear"] },
  "😦": { score: EMOJI_SCORES.LOW_NEGATIVE, emotions: ["fear"] },
  "😬": { score: EMOJI_SCORES.LOW_NEGATIVE, emotions: ["fear"] },

  // Surprise
  "😮": { score: EMOJI_SCORES.SLIGHT_POSITIVE, emotions: ["surprise"] },
  "😯": { score: EMOJI_SCORES.SLIGHT_POSITIVE, emotions: ["surprise"] },
  "😲": { score: EMOJI_SCORES.SLIGHT_POSITIVE, emotions: ["surprise"] },
  "🤯": { score: EMOJI_SCORES.LOW_POSITIVE, emotions: ["surprise"] },
  "😳": { score: EMOJI_SCORES.NEUTRAL, emotions: ["surprise"] },

  // Disgust
  "🤢": { score: EMOJI_SCORES.MEDIUM_NEGATIVE, emotions: ["disgust"] },
  "🤮": { score: EMOJI_SCORES.HIGH_NEGATIVE, emotions: ["disgust"] },
  "😷": { score: EMOJI_SCORES.LOW_NEGATIVE, emotions: ["disgust"] },
  "🤧": { score: EMOJI_SCORES.LOW_NEGATIVE, emotions: ["disgust"] },

  // Neutral/Thinking
  "🤔": { score: EMOJI_SCORES.NEUTRAL, emotions: ["neutral"] },
  "😐": { score: EMOJI_SCORES.NEUTRAL, emotions: ["neutral"] },
  "😑": { score: EMOJI_SCORES.NEUTRAL, emotions: ["neutral"] },
  "🙄": { score: EMOJI_SCORES.SLIGHT_NEGATIVE, emotions: ["neutral"] },
};

/**
 * Negation words that can flip sentiment
 */
const NEGATION_WORDS = [
  "not",
  "no",
  "never",
  "neither",
  "nobody",
  "nothing",
  "nowhere",
  "don't",
  "doesn't",
  "didn't",
  "won't",
  "wouldn't",
  "shouldn't",
  "couldn't",
  "can't",
  "cannot",
  "isn't",
  "aren't",
  "wasn't",
  "weren't",
  "hasn't",
  "haven't",
  "hadn't",
];

/**
 * Emotion keywords and their associated emotion types with weights
 * FIXED: Removed context-dependent words (blue, down) that cause false positives
 */
const EMOTION_KEYWORDS: Record<
  EmotionType,
  Array<{ word: string; weight: number }>
> = {
  joy: [
    { word: "happy", weight: 3 },
    { word: "joy", weight: 3 },
    { word: "joyful", weight: 3 },
    { word: "delighted", weight: 3 },
    { word: "cheerful", weight: 3 },
    { word: "excited", weight: 3 },
    { word: "thrilled", weight: 4 },
    { word: "ecstatic", weight: 4 },
    { word: "elated", weight: 4 },
    { word: "pleased", weight: 2 },
    { word: "glad", weight: 2 },
    { word: "content", weight: 2 },
    { word: "satisfied", weight: 2 },
    { word: "wonderful", weight: 3 },
    { word: "fantastic", weight: 3 },
    { word: "amazing", weight: 3 },
    { word: "awesome", weight: 3 },
    { word: "excellent", weight: 3 },
    { word: "great", weight: 2 },
    { word: "good", weight: 2 },
    { word: "love", weight: 4 },
    { word: "yay", weight: 2 },
  ],
  sadness: [
    { word: "sad", weight: 3 },
    { word: "unhappy", weight: 3 },
    { word: "depressed", weight: 4 },
    { word: "miserable", weight: 4 },
    { word: "gloomy", weight: 3 },
    { word: "melancholy", weight: 3 },
    // Removed "down" and "blue" - context-dependent words
    { word: "disappointed", weight: 3 },
    { word: "heartbroken", weight: 4 },
    { word: "grief", weight: 4 },
    { word: "sorrow", weight: 4 },
    { word: "crying", weight: 3 },
    { word: "tears", weight: 3 },
    { word: "lonely", weight: 3 },
    { word: "alone", weight: 2 },
  ],
  anger: [
    { word: "angry", weight: 3 },
    { word: "mad", weight: 3 },
    { word: "furious", weight: 4 },
    { word: "enraged", weight: 4 },
    { word: "irate", weight: 4 },
    { word: "annoyed", weight: 2 },
    { word: "irritated", weight: 2 },
    { word: "frustrated", weight: 3 },
    { word: "outraged", weight: 4 },
    { word: "hate", weight: 4 },
    { word: "hatred", weight: 4 },
    { word: "despise", weight: 4 },
    { word: "loathe", weight: 4 },
  ],
  fear: [
    { word: "afraid", weight: 3 },
    { word: "scared", weight: 3 },
    { word: "fearful", weight: 3 },
    { word: "frightened", weight: 3 },
    { word: "terrified", weight: 4 },
    { word: "horrified", weight: 4 },
    { word: "anxious", weight: 3 },
    { word: "worried", weight: 2 },
    { word: "nervous", weight: 2 },
    { word: "panic", weight: 4 },
    { word: "dread", weight: 3 },
    { word: "alarmed", weight: 3 },
  ],
  surprise: [
    { word: "surprised", weight: 3 },
    { word: "amazed", weight: 3 },
    { word: "astonished", weight: 3 },
    { word: "shocked", weight: 3 },
    { word: "startled", weight: 3 },
    { word: "stunned", weight: 3 },
    { word: "unexpected", weight: 2 },
    { word: "wow", weight: 2 },
  ],
  disgust: [
    { word: "disgusted", weight: 3 },
    { word: "revolted", weight: 4 },
    { word: "repulsed", weight: 4 },
    { word: "gross", weight: 3 },
    { word: "nasty", weight: 3 },
    { word: "awful", weight: 3 },
    { word: "terrible", weight: 3 },
    { word: "horrible", weight: 3 },
    { word: "disgusting", weight: 4 },
    { word: "yuck", weight: 3 },
    { word: "ew", weight: 2 },
  ],
  neutral: [
    { word: "okay", weight: 0 },
    { word: "ok", weight: 0 },
    { word: "fine", weight: 0 },
    { word: "alright", weight: 0 },
    { word: "whatever", weight: 0 },
    { word: "meh", weight: 0 },
  ],
};

/**
 * Pre-compiled regex patterns for better performance
 * FIXED: Avoids creating new regex on every function call
 */
const KEYWORD_REGEX_CACHE = new Map<string, RegExp>();

/**
 * Maximum cache size for keyword regex patterns
 * Prevents unbounded memory growth if keyword list expands significantly
 */
const MAX_REGEX_CACHE_SIZE = 500;

/**
 * Maximum input length for sentiment analysis (characters)
 * Prevents potential abuse and ensures reasonable processing times
 */
const MAX_INPUT_LENGTH = 50000;

/**
 * Gets or creates a cached regex for a keyword
 * Returns a cached regex pattern for word boundary matching
 *
 * FIXED: Added cache size limit to prevent unbounded memory growth
 *
 * @param word - The keyword to create a regex pattern for
 * @returns The compiled regex pattern with word boundaries and global case-insensitive flags
 * @throws Never throws - regex is always created if not in cache
 */
function getKeywordRegex(word: string): RegExp {
  let regex = KEYWORD_REGEX_CACHE.get(word);
  if (!regex) {
    // If cache is at max size, clear oldest entries (simple FIFO approach)
    if (KEYWORD_REGEX_CACHE.size >= MAX_REGEX_CACHE_SIZE) {
      // Clear first entry (Map maintains insertion order)
      const firstKey = KEYWORD_REGEX_CACHE.keys().next().value;
      if (firstKey) {
        KEYWORD_REGEX_CACHE.delete(firstKey);
      }
    }
    regex = new RegExp(`\\b${word}\\b`, "gi");
    KEYWORD_REGEX_CACHE.set(word, regex);
  }
  return regex;
}

/**
 * Pre-compiled negation regex pattern
 * NOTE: Current pattern matches "negation + space + word" (e.g., "not happy")
 * Limitations (acceptable for current scope, may enhance in future):
 * - Doesn't handle adverbs: "not very happy", "not really good"
 * - Doesn't handle double negation: "not unhappy"
 * - Doesn't handle phrasal negation: "by no means good"
 */
const NEGATION_REGEX = new RegExp(
  `\\b(${NEGATION_WORDS.join("|")})\\b\\s+\\w+`,
  "gi",
);

/**
 * Cached emoji regex for performance
 * FIXED: Cache at module level instead of creating new instance on each call
 */
const EMOJI_REGEX = emojiRegex();

/**
 * Constants for confidence calculation
 * Extracted for maintainability and clarity
 */
const CONFIDENCE_CONSTANTS = {
  // Word count contribution
  WORDS_PER_MAX_CONFIDENCE: 10, // 10 words = 0.5 confidence (capped)
  MAX_WORD_CONFIDENCE: 0.5,
  MIN_WORD_CONFIDENCE: 0.1,

  // Emoji contribution
  EMOJIS_PER_MAX_CONFIDENCE: 3, // 3 emojis = 0.6 confidence
  MAX_EMOJI_ONLY_CONFIDENCE: 0.6,
  EMOJI_BOOST_PER_EMOJI: 0.05,
  MAX_EMOJI_BOOST: 0.15,

  // Emotion clarity contribution
  SINGLE_EMOTION_BOOST: 0.3,
  TWO_EMOTION_BOOST: 0.2,
  MULTI_EMOTION_BOOST: 0.1,

  // Emotion strength contribution
  SCORE_PER_MAX_BOOST: 20, // Score of 20 = 0.2 boost
  MAX_STRENGTH_BOOST: 0.2,

  // Clear single emotion (emoji-only case)
  CLEAR_EMOTION_BOOST: 0.3,
  MAX_CLEAR_EMOJI_CONFIDENCE: 0.9,
} as const;

/**
 * Analyzes emojis in text and returns their combined sentiment score and emotions
 * FIXED: Now uses emoji-regex library for proper emoji parsing to avoid
 * matching individual emoji components (e.g., family emojis as multiple emojis)
 */
function analyzeEmojis(text: string): {
  score: number;
  emotions: Map<EmotionType, number>;
  count: number;
} {
  const emotions = new Map<EmotionType, number>();
  let score = 0;
  let count = 0;

  // Use emoji-regex library for accurate emoji matching
  // This properly handles multi-codepoint emojis like family emojis (👨‍👩‍👧‍👦)
  // Use cached regex for better performance
  const matches = text.matchAll(EMOJI_REGEX);

  for (const match of matches) {
    const emoji = match[0];
    count++; // Count all emojis for confidence calculation
    const emojiData = EMOJI_SENTIMENT[emoji];
    if (emojiData) {
      score += emojiData.score;
      for (const emotion of emojiData.emotions) {
        // NOTE: Using Math.abs() here means both positive and negative emojis
        // contribute to emotion intensity scoring. This is intentional:
        // - We want to track how STRONGLY an emotion is expressed
        // - A score of -4 (very sad) is as intense as +4 (very happy)
        // - The actual sentiment direction is preserved in the score
        // - This approach helps rank emotions by their strength/clarity
        emotions.set(
          emotion,
          (emotions.get(emotion) || 0) + Math.abs(emojiData.score),
        );
      }
    }
  }

  return { score, emotions, count };
}

/**
 * Detects positions of negated words in text
 * FIXED: Now tracks negation per-instance instead of globally per-word
 * This prevents incorrectly filtering non-negated uses of the same word
 * e.g., "I'm not happy, but I'm happy now" correctly counts the second "happy"
 *
 * FIXED: Accepts pre-lowercased text to avoid redundant toLowerCase() calls
 *
 * @param lowerText - The lowercase version of the text to analyze
 * @returns Set of character positions where negated words start
 */
function getNegatedPositions(lowerText: string): Set<number> {
  const negatedPositions = new Set<number>();

  // Find all negation patterns (e.g., "not happy", "don't like")
  const matches = lowerText.matchAll(NEGATION_REGEX);
  for (const match of matches) {
    // Track the position where the negated word starts
    // The negation pattern is "negation_word + space + target_word"
    const negationMatch = match[0];
    const matchIndex = match.index!;

    // Find where the word after negation starts
    const parts = negationMatch.split(/\s+/);
    if (parts.length >= 2) {
      // Calculate position: match start + length of negation word + spaces
      const wordAfterNegationStart = matchIndex + parts[0].length + 1;
      negatedPositions.add(wordAfterNegationStart);
    }
  }

  return negatedPositions;
}

/**
 * Analyzes text for specific emotion keywords
 * FIXED: Now handles negation per-instance instead of globally
 * Properly handles cases like "not happy, but happy now"
 *
 * FIXED: Optimized to perform toLowerCase() once and reuse result
 *
 * @param text - The text to analyze
 * @returns Map of emotion types to their cumulative scores
 */
function analyzeEmotionKeywords(text: string): Map<EmotionType, number> {
  const emotions = new Map<EmotionType, number>();
  // FIXED: Single toLowerCase() call, result reused throughout function
  const lowerText = text.toLowerCase();
  const negatedPositions = getNegatedPositions(lowerText);

  for (const [emotionType, keywords] of Object.entries(EMOTION_KEYWORDS)) {
    let emotionScore = 0;
    for (const { word, weight } of keywords) {
      // Use pre-compiled regex for better performance
      // FIXED: Reset lastIndex to ensure consistent matching with global flag
      const regex = getKeywordRegex(word);
      regex.lastIndex = 0; // Reset for global regex reuse

      let match: RegExpExecArray | null;
      // Use exec() for proper stateful matching with global regex
      while ((match = regex.exec(lowerText)) !== null) {
        const matchPosition = match.index;

        // Skip only this specific instance if it's at a negated position
        if (negatedPositions.has(matchPosition)) {
          continue;
        }

        // Count this non-negated instance
        emotionScore += weight;
      }
    }

    // FIXED: Simplified condition - emotionScore > 0 is clearer
    // Special handling for neutral keywords below (since they have weight 0)
    if (emotionScore > 0) {
      emotions.set(emotionType as EmotionType, emotionScore);
    }
    // Special handling for neutral keywords - track them even with 0 score
    if (emotionType === "neutral" && emotionScore === 0) {
      // Check if any neutral keywords were actually matched (and not all negated)
      const hasNonNegatedMatch = keywords.some(({ word }) => {
        const regex = getKeywordRegex(word);
        regex.lastIndex = 0; // Reset for reuse

        let match: RegExpExecArray | null;
        while ((match = regex.exec(lowerText)) !== null) {
          if (!negatedPositions.has(match.index)) {
            return true; // Found a non-negated match
          }
        }
        return false;
      });
      if (hasNonNegatedMatch) {
        emotions.set("neutral", 1); // Use minimal score to indicate presence
      }
    }
  }

  return emotions;
}

/**
 * Determines intensity level based on score
 */
function getIntensity(score: number): IntensityLevel {
  const absScore = Math.abs(score);
  if (absScore >= 8) {
    return "high";
  }
  if (absScore >= 4) {
    return "medium";
  }
  return "low";
}

/**
 * Calculates confidence score based on various factors
 * FIXED: Now handles edge cases:
 * - Empty text or no words
 * - Single emoji without text
 * - Multiple emojis without text
 * - Very short text
 *
 * Uses named constants for all thresholds to improve maintainability
 */
function calculateConfidence(
  wordCount: number,
  emotionCount: number,
  maxEmotionScore: number,
  emojiCount: number,
): number {
  const C = CONFIDENCE_CONSTANTS;

  // Handle edge case: empty text
  if (wordCount === 0 && emojiCount === 0) {
    return 0;
  }

  // Handle edge case: only emojis, no text
  if (wordCount === 0 && emojiCount > 0) {
    // Confidence based on number of emojis
    // Single emoji = lower confidence, multiple emojis = higher confidence
    const emojiConfidence = Math.min(
      emojiCount / C.EMOJIS_PER_MAX_CONFIDENCE,
      C.MAX_EMOJI_ONLY_CONFIDENCE,
    );

    // Boost confidence if emotion is clear (single distinct emotion)
    if (emotionCount === 1) {
      return Math.min(
        emojiConfidence + C.CLEAR_EMOTION_BOOST,
        C.MAX_CLEAR_EMOJI_CONFIDENCE,
      );
    }
    return emojiConfidence;
  }

  // Base confidence on word count (more words = more confidence, up to a point)
  // Minimum confidence for very short text
  let confidence = Math.max(
    Math.min(wordCount / C.WORDS_PER_MAX_CONFIDENCE, C.MAX_WORD_CONFIDENCE),
    C.MIN_WORD_CONFIDENCE,
  );

  // Factor in emoji count for mixed emoji+text content
  if (emojiCount > 0) {
    confidence += Math.min(
      emojiCount * C.EMOJI_BOOST_PER_EMOJI,
      C.MAX_EMOJI_BOOST,
    );
  }

  // Add confidence based on emotion clarity (clear dominant emotion)
  if (emotionCount === 1) {
    confidence += C.SINGLE_EMOTION_BOOST;
  } else if (emotionCount === 2) {
    confidence += C.TWO_EMOTION_BOOST;
  } else if (emotionCount > 2) {
    confidence += C.MULTI_EMOTION_BOOST;
  }

  // Add confidence based on emotion strength
  confidence += Math.min(
    maxEmotionScore / C.SCORE_PER_MAX_BOOST,
    C.MAX_STRENGTH_BOOST,
  );

  return Math.min(confidence, 1);
}

/**
 * Analyzes the sentiment of a given text with enhanced emotion detection
 *
 * **Performance Characteristics:**
 * - Current: Multi-pass implementation (base sentiment + emoji + keyword analysis)
 * - Typical: ~0.5ms for short messages (< 20 words)
 * - Stress: < 500ms for 1000 words, < 2s for 5000 words
 * - Emoji-heavy: < 300ms for 700 emojis
 *
 * **Future Optimization Consideration:**
 * For applications requiring analysis of extremely long documents (10,000+ words),
 * consider implementing a single-pass parser that:
 * 1. Tokenizes text once
 * 2. Simultaneously detects emojis, keywords, and negations in one pass
 * 3. Maintains position tracking without multiple regex scans
 *
 * Current multi-pass approach is well-suited for chatbot messages and provides
 * better code maintainability and clarity.
 *
 * @param text - The text to analyze
 * @returns Enhanced sentiment analysis result with emotions, intensity, and confidence
 * @throws Error if input text exceeds maximum length
 */
export function analyzeSentiment(text: string): SentimentResult {
  // FIXED: Add input length validation for security
  if (text.length > MAX_INPUT_LENGTH) {
    throw new Error(
      `Input text exceeds maximum length of ${MAX_INPUT_LENGTH} characters`,
    );
  }

  // Get base sentiment score from the sentiment library
  const baseSentiment = sentiment.analyze(text);

  // Analyze emojis
  const emojiAnalysis = analyzeEmojis(text);

  // Analyze emotion keywords
  const keywordEmotions = analyzeEmotionKeywords(text);

  // Combine emoji and keyword emotions
  const combinedEmotions = new Map<EmotionType, number>();

  // Merge emoji emotions
  for (const [emotion, score] of emojiAnalysis.emotions) {
    combinedEmotions.set(emotion, (combinedEmotions.get(emotion) || 0) + score);
  }

  // Merge keyword emotions
  for (const [emotion, score] of keywordEmotions) {
    combinedEmotions.set(emotion, (combinedEmotions.get(emotion) || 0) + score);
  }

  // Calculate overall score (base + emoji contribution)
  const overallScore = baseSentiment.score + emojiAnalysis.score;

  // Convert emotions to array and sort by score
  const emotionArray: Emotion[] = Array.from(combinedEmotions.entries())
    .map(([type, score]) => ({
      type,
      intensity: getIntensity(score),
      score,
    }))
    .sort((a, b) => b.score - a.score);

  // Determine dominant emotion
  let dominantEmotion: EmotionType = "neutral";
  if (emotionArray.length > 0) {
    dominantEmotion = emotionArray[0].type;
  } else {
    // If no specific emotions detected, infer from overall score
    if (overallScore > 0) {
      dominantEmotion = "joy";
    } else if (overallScore < 0) {
      dominantEmotion = "sadness";
    }
  }

  // Calculate confidence
  const wordCount = baseSentiment.tokens.length;
  const maxEmotionScore = emotionArray.length > 0 ? emotionArray[0].score : 0;
  const confidence = calculateConfidence(
    wordCount,
    emotionArray.length,
    maxEmotionScore,
    emojiAnalysis.count,
  );

  return {
    score: overallScore,
    comparative: baseSentiment.comparative,
    emotions: emotionArray,
    dominantEmotion,
    confidence,
  };
}

/**
 * Legacy function for backward compatibility
 * Returns just the sentiment score as before
 * @deprecated Use analyzeSentiment() instead for enhanced results
 */
export function getSentimentScore(text: string): number {
  return analyzeSentiment(text).score;
}
